import { Link } from "react-router";

import { useBrandName } from "@/api/hooks/tenant";
import { useMe } from "@/api/hooks/auth";
import { Logo } from "@/components/common/logo";
import { ScrollArea } from "@/components/ui/scroll-area";

import { APP_HOME, PEOPLE_NAV, PRIMARY_NAV, SETTINGS_NAV, type NavItem } from "./nav-config";
import { SidebarCourses } from "./sidebar-courses";
import { SidebarNavLink } from "./sidebar-link";

/** Fixed desktop sidebar (lg and up). Below lg the same content lives in the mobile nav sheet. */
export function Sidebar() {
  return (
    <div
      data-app-sidebar
      className="fixed inset-y-0 left-0 z-30 hidden w-60 border-r border-sidebar-border bg-sidebar text-sidebar-foreground lg:block"
    >
      <SidebarContent />
    </div>
  );
}

interface SidebarContentProps {
  /** Called after any link is followed (lets the mobile sheet close). */
  onNavigate?: () => void;
}

export function SidebarContent({ onNavigate }: SidebarContentProps) {
  const brand = useBrandName();
  const me = useMe();
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-15 shrink-0 items-center px-5">
        <Link
          to={APP_HOME}
          onClick={onNavigate}
          aria-label={`${brand} home`}
          className="flex rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
        >
          <Logo size="sm" />
        </Link>
      </div>
      <nav aria-label="Main" className="flex min-h-0 flex-1 flex-col">
        {/* Radix renders the viewport content as display:table, which defeats text truncation. */}
        <ScrollArea className="min-h-0 flex-1">
          <div className="space-y-6 px-3 pt-2 pb-4">
            <NavList
              items={me?.role === "instructor" ? [...PRIMARY_NAV, PEOPLE_NAV] : PRIMARY_NAV}
              onNavigate={onNavigate}
            />
            <SidebarCourses onNavigate={onNavigate} />
          </div>
        </ScrollArea>
        <div className="shrink-0 border-t border-sidebar-border p-3 pb-2">
          <NavList items={[SETTINGS_NAV]} onNavigate={onNavigate} />
        </div>
      </nav>
      {/* Who's signed in and the account actions live in the top bar's account menu. */}
    </div>
  );
}

function NavList({ items, onNavigate }: { items: readonly NavItem[]; onNavigate?: () => void }) {
  return (
    <ul className="space-y-0.5">
      {items.map((item) => (
        <li key={item.to}>
          <SidebarNavLink to={item.to} end={item.end} onNavigate={onNavigate} indicator icon={item.icon}>
            {item.label}
          </SidebarNavLink>
        </li>
      ))}
    </ul>
  );
}
